import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, CloudUpload, Lock, LogOut, TriangleAlert } from 'lucide-react'
import { Role } from '@shared/types'
import type { DropdownMenuItem } from '@/components/ui'
import ChangePasswordDialog from '@/components/auth/ChangePasswordDialog'
import { Badge, Button, DropdownMenu, Modal } from '@/components/ui'
import { formatNumber, pluralize } from '@/lib/format'
import { useAuthStore } from '@/stores/useAuthStore'
import { forgetSessionWork, useSavePath, useUnsavedCount } from '@/stores/useSessionGuard'

function initials(name: string, email: string): string {
  const parts = (name.trim() || email.trim()).split(/[\s@._-]+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return `${first}${last}`.toUpperCase()
}

export default function UserMenu(): React.ReactElement | null {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const unsavedCount = useUnsavedCount()
  const savePath = useSavePath()
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const prompted = useRef(false)

  // An admin-reset account arrives with `mustChangePassword`; surface the dialog
  // once instead of waiting for the user to discover the menu.
  useEffect(() => {
    if (user?.mustChangePassword && !prompted.current) {
      prompted.current = true
      setPasswordOpen(true)
    }
  }, [user?.mustChangePassword])

  // The count is live: a save job landing while the dialog is open removes the
  // only reason to have asked.
  useEffect(() => {
    if (unsavedCount === 0) setConfirmOpen(false)
  }, [unsavedCount])

  if (!user) return null

  const signOut = async (): Promise<void> => {
    setSigningOut(true)
    try {
      await logout()
    } finally {
      // The server has deleted the rows; forgetting them here stops the next
      // sign-in in this tab from warning about work that no longer exists.
      forgetSessionWork()
      navigate('/login', { replace: true })
    }
  }

  // Signing out deletes the session's site and every unsaved alt text under it,
  // so it only goes straight through when there is nothing left to lose.
  const requestSignOut = (): void => {
    if (unsavedCount > 0) {
      setConfirmOpen(true)
      return
    }
    void signOut()
  }

  const items: DropdownMenuItem[] = [
    { label: user.email, onSelect: () => {}, disabled: true },
    { separator: true },
    {
      label: 'Change password',
      icon: <Lock className="h-4 w-4" aria-hidden="true" />,
      onSelect: () => setPasswordOpen(true)
    },
    { separator: true }
  ]

  if (unsavedCount > 0) {
    // Read-only, but it puts the stake in front of the user before they click.
    items.push({
      label: `${formatNumber(unsavedCount)} unsaved ${pluralize(unsavedCount, 'image')}`,
      icon: <TriangleAlert className="h-4 w-4" aria-hidden="true" />,
      disabled: true,
      onSelect: () => {}
    })
  }

  items.push({
    label: 'Sign out',
    icon: <LogOut className="h-4 w-4" aria-hidden="true" />,
    danger: true,
    onSelect: requestSignOut
  })

  return (
    <>
      <DropdownMenu
        align="end"
        trigger={
          <button
            type="button"
            aria-label={`Account menu for ${user.email}`}
            className="flex max-w-[14rem] items-center gap-2 rounded-control border border-transparent py-1 pr-1.5 pl-1 text-left transition-colors hover:border-line hover:bg-sunken"
          >
            <span
              aria-hidden="true"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent"
            >
              {initials(user.name, user.email)}
            </span>
            <span className="hidden min-w-0 sm:block">
              <span className="block truncate text-[13px] leading-tight font-medium text-fg">
                {user.name || user.email}
              </span>
              <span className="block truncate text-[11px] leading-tight text-fg-subtle">
                {user.email}
              </span>
            </span>
            {user.role === Role.Admin && (
              <span className="hidden md:inline-flex">
                <Badge variant="accent" size="sm">
                  Admin
                </Badge>
              </span>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
          </button>
        }
        items={items}
      />

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!signingOut) setConfirmOpen(false)
        }}
        title="Sign out and delete this work?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={signingOut}>
              Keep working
            </Button>
            {/* Destructive, but deliberately the quieter of the two real choices. */}
            <Button
              variant="secondary"
              className="text-bad-600 dark:text-bad-300"
              iconLeft={<LogOut />}
              onClick={() => {
                void signOut()
              }}
              loading={signingOut}
            >
              Sign out anyway
            </Button>
            <Button
              data-autofocus
              iconLeft={<CloudUpload />}
              disabled={signingOut}
              onClick={() => {
                setConfirmOpen(false)
                navigate(savePath)
              }}
            >
              Save to WordPress first
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-fg-muted">
          <strong className="font-semibold text-fg">
            {formatNumber(unsavedCount)} {pluralize(unsavedCount, 'image')}
          </strong>{' '}
          {unsavedCount === 1 ? 'has' : 'have'} alt text that has not been written to WordPress.
        </p>

        <div className="mt-4 flex items-start gap-3 rounded-card border border-bad-500/35 bg-bad-100/60 p-3 dark:bg-bad-950/50">
          <TriangleAlert
            className="mt-px size-4 shrink-0 text-bad-700 dark:text-bad-300"
            aria-hidden="true"
          />
          <p className="text-xs leading-relaxed text-fg-muted">
            Signing out ends this session: the connected site, everything fetched from it and all{' '}
            {formatNumber(unsavedCount)} unsaved alt {pluralize(unsavedCount, 'tag')} are deleted and
            cannot be recovered. Alt text already saved to WordPress stays there, and so do your
            account and SSH keys.
          </p>
        </div>

        <p className="mt-3 text-xs leading-relaxed text-fg-subtle">
          Only approved alt text is written, so anything still waiting on approval needs a pass on
          the media grid before it can be saved.
        </p>
      </Modal>

      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  )
}

export { UserMenu }
